import { useState, useEffect } from 'react';
import { Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import QRCode from 'qrcode';

export default function QRCodeTab() {
  const [qrData, setQrData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [targetUrl, setTargetUrl] = useState('');

  useEffect(() => {
    // Generate QR code on frontend using the actual browser URL
    const generateQR = async () => {
      try {
        // Use the actual domain the user is on
        const baseUrl = window.location.origin;
        const orderUrl = `${baseUrl}/order`;
        setTargetUrl(orderUrl);
        
        // Generate QR code as data URL
        const qrDataUrl = await QRCode.toDataURL(orderUrl, {
          width: 300,
          margin: 2,
          color: {
            dark: '#0D9488',
            light: '#FFFFFF'
          },
          errorCorrectionLevel: 'H'
        });
        
        setQrData({ base64: qrDataUrl, url: orderUrl });
        setLoading(false);
      } catch (err) {
        console.error('QR generation error:', err);
        setLoading(false);
      }
    };
    
    generateQR();
  }, []);

  const downloadQR = async () => {
    try {
      // Generate high-res QR for download
      const qrDataUrl = await QRCode.toDataURL(targetUrl, {
        width: 600,
        margin: 2,
        color: {
          dark: '#0D9488',
          light: '#FFFFFF'
        },
        errorCorrectionLevel: 'H'
      });
      
      // Convert data URL to blob and download
      const response = await fetch(qrDataUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'bulaccino-qr-code.png';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('QR Code downloaded!');
    } catch (err) {
      toast.error('Failed to download QR code');
    }
  };

  return (
    <div data-testid="qrcode-tab">
      <h1 className="font-heading text-3xl mb-8 text-foreground">QR Code for Mobile Ordering</h1>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* QR Code Display */}
        <div className="bg-card rounded-2xl p-8 shadow-sm text-center border border-border">
          <h2 className="font-heading text-xl mb-6 text-foreground">Your Ordering QR Code</h2>
          
          {loading ? (
            <div className="w-64 h-64 bg-muted rounded-2xl animate-pulse mx-auto" />
          ) : qrData ? (
            <div className="space-y-6">
              <div className="inline-block p-6 bg-white dark:bg-gray-100 rounded-2xl shadow-lg border-4 border-primary/20">
                <img 
                  src={qrData.base64} 
                  alt="Bulaccino Cafe QR Code"
                  className="w-64 h-64"
                  data-testid="qr-code-image"
                />
              </div>
              <p className="text-sm text-muted-foreground">
                Points to: {qrData.url}
              </p>
            </div>
          ) : (
            <p className="text-muted-foreground">Failed to generate QR code</p>
          )}

          <div className="flex gap-3 justify-center mt-6">
            <Button 
              onClick={downloadQR}
              className="rounded-full bg-primary hover:bg-primary/90"
              data-testid="download-qr-png"
            >
              <Download className="w-4 h-4 mr-2" />
              Download PNG
            </Button>
          </div>
        </div>

        {/* Usage Instructions */}
        <div className="bg-card rounded-2xl p-8 shadow-sm border border-border">
          <h2 className="font-heading text-xl mb-6 text-foreground">How to Use</h2>
          
          <div className="space-y-6">
            <div className="flex gap-4">
              <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center shrink-0">
                <span className="font-bold text-primary">1</span>
              </div>
              <div>
                <h3 className="font-medium mb-1 text-foreground">Print & Display</h3>
                <p className="text-sm text-muted-foreground">
                  Download the QR code and print it. Place it on tables, counters, or at the entrance of your cafe.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center shrink-0">
                <span className="font-bold text-primary">2</span>
              </div>
              <div>
                <h3 className="font-medium mb-1 text-foreground">Customers Scan</h3>
                <p className="text-sm text-muted-foreground">
                  Customers scan the code with their phone camera to instantly access the mobile ordering page.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center shrink-0">
                <span className="font-bold text-primary">3</span>
              </div>
              <div>
                <h3 className="font-medium mb-1 text-foreground">Order & Pay</h3>
                <p className="text-sm text-muted-foreground">
                  They can browse the menu, customize their drinks, and place orders directly from their phone.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center shrink-0">
                <span className="font-bold text-primary">4</span>
              </div>
              <div>
                <h3 className="font-medium mb-1 text-foreground">Pick Up</h3>
                <p className="text-sm text-muted-foreground">
                  Orders appear in your dashboard. Customers get SMS notifications when their order is ready.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-8 p-4 bg-primary/10 rounded-xl border border-primary/20">
            <h3 className="font-medium text-primary mb-2">Pro Tips</h3>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• Use a larger size (600x600) for printed materials</li>
              <li>• Consider adding your logo next to the QR code</li>
              <li>• Test the QR code before printing in bulk</li>
              <li>• Update the custom URL in Settings if needed</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
